import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { validateFieldValue, type FieldValidationRule as SharedFieldValidationRule } from '../utils/validationMessages';
import { normalizePickListValue } from '../utils/soteriaPickListNormalize';
import {
  isSupportedDataroomFileName,
  unsupportedDataroomFileMessage,
} from '../utils/dataroomFileTypes';
import './GenerateDatatapeFromBoxDataroom.css';

interface BoxFolderItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: number;
}

interface SelectedFile {
  id: string;
  name: string;
}

interface FileRecommendation {
  fileId: string;
  fileName: string;
  relativePath: string;
  ancestorFolderIds: string[];
  documentType: string;
  confidence: string;
}

interface RecommendedFileMeta {
  relativePath: string;
  documentType: string;
}

interface FieldValidationRule {
  kind: string;
  nullable: boolean;
  integer?: boolean;
  year?: boolean;
  min?: number;
  max?: number;
  occupancyStatusText?: boolean;
}

interface ConditionalAllowedValues {
  when: { field: string; equals: string };
  values: string[];
}

interface ExtractedField {
  columnIndex: number;
  fieldName: string;
  value: string | null;
  rawValue: string | null;
  fileId: string | null;
  fileName: string | null;
  fileUrl: string | null;
  error: string | null;
  allowedValues?: string[];
  validationRule?: FieldValidationRule;
  conditionalAllowedValues?: ConditionalAllowedValues | null;
}

type ExtractionPhase = 'idle' | 'extracting' | 'review' | 'generating';
type EditingConfigField = 'projectId' | 'folderUrl';

interface FolderContents {
  name: string;
  items: BoxFolderItem[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const TREE_INDENT_PX = 20;
const MAX_SELECTED_FILES = 4;
const FILE_SELECTION_LIMIT_MESSAGE = `You can select up to ${MAX_SELECTED_FILES} files for analysis. Deselect a file to choose a different one.`;

type GptProgressStepId = 'downloading' | 'analyzing' | 'importing' | 'normalizing';
type GptProgressStepStatus = 'pending' | 'active' | 'done' | 'error';

interface GptProgressStep {
  id: GptProgressStepId;
  label: string;
  status: GptProgressStepStatus;
  detail?: string;
}

const GPT_PROGRESS_STEP_DEFS: Array<{ id: GptProgressStepId; label: string }> = [
  { id: 'downloading', label: 'Downloading selected files' },
  { id: 'analyzing', label: 'Analyzing files' },
  { id: 'importing', label: 'Importing data' },
  { id: 'normalizing', label: 'Normalizing data' },
];

function createInitialGptSteps(): GptProgressStep[] {
  return GPT_PROGRESS_STEP_DEFS.map((step) => ({
    ...step,
    status: 'pending',
  }));
}

function applyGptProgressEvent(
  steps: GptProgressStep[],
  event: { step: GptProgressStepId; status: 'active' | 'done' | 'error'; detail?: string }
): GptProgressStep[] {
  const stepOrder = GPT_PROGRESS_STEP_DEFS.map((s) => s.id);
  const activeIndex = stepOrder.indexOf(event.step);

  return steps.map((step) => {
    const stepIndex = stepOrder.indexOf(step.id);
    if (step.id === event.step) {
      return {
        ...step,
        status:
          event.status === 'active' ? 'active' : event.status === 'done' ? 'done' : 'error',
        detail: event.detail ?? step.detail,
      };
    }
    if (event.status === 'active' && stepIndex < activeIndex && step.status !== 'done') {
      return { ...step, status: 'done' };
    }
    return step;
  });
}

function gptProgressPercent(steps: GptProgressStep[]): number {
  const total = steps.length;
  const completed = steps.filter((s) => s.status === 'done').length;
  const hasActive = steps.some((s) => s.status === 'active');
  const progress = completed + (hasActive ? 0.4 : 0);
  return Math.min(100, Math.round((progress / total) * 100));
}

type FolderLoadStepId = 'loading-folder' | 'finding-documents';
type FolderLoadStepStatus = 'pending' | 'active' | 'done';

interface FolderLoadStep {
  id: FolderLoadStepId;
  label: string;
  status: FolderLoadStepStatus;
}

const FOLDER_LOAD_STEP_DEFS: Array<{ id: FolderLoadStepId; label: string }> = [
  { id: 'loading-folder', label: 'Loading folder contents' },
  { id: 'finding-documents', label: 'Finding recommended documents' },
];

function getEffectiveAllowedValues(
  field: ExtractedField,
  allFields: ExtractedField[]
): string[] {
  if (field.allowedValues && field.allowedValues.length > 0) {
    return field.allowedValues;
  }
  if (field.conditionalAllowedValues) {
    const { when, values } = field.conditionalAllowedValues;
    const parentField = allFields.find((f) => f.fieldName.trim() === when.field);
    const parentValue = parentField?.value?.trim() ?? '';
    if (parentValue === when.equals) {
      return values;
    }
  }
  return [];
}

function buildFieldEditErrors(fields: ExtractedField[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const allowedValues = getEffectiveAllowedValues(field, fields);
    const validationError = validateFieldValue(
      field.value ?? '',
      allowedValues,
      field.validationRule as SharedFieldValidationRule | undefined
    );
    if (validationError) {
      errors[String(field.columnIndex)] = validationError;
    }
  }
  return errors;
}

function resolvePickListFieldValue(
  field: ExtractedField,
  allFields: ExtractedField[]
): ExtractedField {
  const value = field.value?.trim() ?? '';
  if (!value) return field;

  const allowedValues = getEffectiveAllowedValues(field, allFields);
  if (allowedValues.length === 0) return field;

  const resolved = normalizePickListValue(value, allowedValues, field.fieldName.trim());
  if (resolved) {
    return { ...field, value: resolved, error: null };
  }
  return field;
}

function resolveExtractedFieldsForReview(fields: ExtractedField[]): ExtractedField[] {
  let resolved = [...fields];
  for (let pass = 0; pass < 2; pass++) {
    resolved = resolved.map((field) => resolvePickListFieldValue(field, resolved));
  }
  return resolved;
}

function buildValuesByColumnFromFields(fields: ExtractedField[]): Record<string, string> {
  const valuesByColumn: Record<string, string> = {};
  for (const field of fields) {
    if (field.value !== null && field.value.trim() !== '') {
      valuesByColumn[String(field.columnIndex)] = field.value;
    }
  }
  return valuesByColumn;
}

function createInitialFolderLoadSteps(): FolderLoadStep[] {
  return FOLDER_LOAD_STEP_DEFS.map((step) => ({
    ...step,
    status: 'pending',
  }));
}

function setFolderLoadStepActive(steps: FolderLoadStep[], stepId: FolderLoadStepId): FolderLoadStep[] {
  const stepOrder = FOLDER_LOAD_STEP_DEFS.map((s) => s.id);
  const activeIndex = stepOrder.indexOf(stepId);

  return steps.map((step) => {
    const stepIndex = stepOrder.indexOf(step.id);
    if (step.id === stepId) {
      return { ...step, status: 'active' };
    }
    if (stepIndex < activeIndex) {
      return { ...step, status: 'done' };
    }
    return step;
  });
}

function completeFolderLoadSteps(steps: FolderLoadStep[]): FolderLoadStep[] {
  return steps.map((step) => ({ ...step, status: 'done' }));
}

function folderLoadProgressPercent(steps: FolderLoadStep[]): number {
  const total = steps.length;
  const completed = steps.filter((s) => s.status === 'done').length;
  const hasActive = steps.some((s) => s.status === 'active');
  const progress = completed + (hasActive ? 0.4 : 0);
  return Math.min(100, Math.round((progress / total) * 100));
}

function CircularProgress({ percent }: { percent: number }) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="gpt-progress-ring" aria-hidden="true">
      <svg width="112" height="112" viewBox="0 0 112 112">
        <circle
          className="gpt-progress-ring-track"
          cx="56"
          cy="56"
          r={radius}
          fill="none"
          strokeWidth="8"
        />
        <circle
          className="gpt-progress-ring-fill"
          cx="56"
          cy="56"
          r={radius}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 56 56)"
        />
      </svg>
      <span className="gpt-progress-ring-label">{percent}%</span>
    </div>
  );
}

function formatGptExtractError(err: unknown): string {
  if (!(err instanceof Error)) {
    return 'Failed to extract data with GPT.';
  }
  const msg = err.message.toLowerCase();
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('network request failed')
  ) {
    return (
      'Connection lost while analyzing files (likely a timeout). ' +
      'Try again with fewer files, or increase OPENAI_TIMEOUT_MS_DATATAPE_EXTRACT and App Runner request timeout.'
    );
  }
  if (msg.includes('gpt extraction ended without a result')) {
    return (
      'Connection closed before extraction finished (likely a proxy or App Runner timeout). ' +
      'Try again with fewer files or ask your admin to raise the App Runner request timeout.'
    );
  }
  return err.message;
}

async function consumeGptExtractStream(
  response: Response,
  onProgress: (event: {
    step: GptProgressStepId;
    status: 'active' | 'done' | 'error';
    detail?: string;
  }) => void
): Promise<{ fields: ExtractedField[]; valuesByColumn: Record<string, string> }> {
  if (!response.ok) {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text) as { error?: string };
      throw new Error(parsed.error || 'Failed to extract data with GPT.');
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr.message !== 'Failed to extract data with GPT.') {
        throw parseErr;
      }
      throw new Error(text || 'Failed to extract data with GPT.');
    }
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response stream from server.');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let result: { fields: ExtractedField[]; valuesByColumn: Record<string, string> } | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      for (const line of chunk.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6)) as {
          type: string;
          step?: GptProgressStepId;
          status?: 'active' | 'done' | 'error';
          detail?: string;
          error?: string;
          fields?: ExtractedField[];
          valuesByColumn?: Record<string, string>;
        };

        if (payload.type === 'progress' && payload.step && payload.status) {
          onProgress({
            step: payload.step,
            status: payload.status,
            detail: payload.detail,
          });
        } else if (payload.type === 'complete' && payload.fields) {
          result = {
            fields: payload.fields,
            valuesByColumn: payload.valuesByColumn ?? {},
          };
        } else if (payload.type === 'error') {
          throw new Error(payload.error || 'GPT extraction failed.');
        }
      }

      boundary = buffer.indexOf('\n\n');
    }
  }

  if (!result) {
    throw new Error('GPT extraction ended without a result.');
  }
  return result;
}

function FolderIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function GenerateDatatapeFromBoxDataroom() {
  const [soteriaProjectId, setSoteriaProjectId] = useState('');
  const [boxFolderUrl, setBoxFolderUrl] = useState('');
  const [savedBoxFolderUrl, setSavedBoxFolderUrl] = useState('');
  const [rootFolder, setRootFolder] = useState<{ id: string; name: string } | null>(null);
  const [folderContents, setFolderContents] = useState<Record<string, FolderContents>>({});
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [isLoadingRoot, setIsLoadingRoot] = useState(false);
  const [phase, setPhase] = useState<ExtractionPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [selectedFilesById, setSelectedFilesById] = useState<Record<string, SelectedFile>>({});
  const [extractedFields, setExtractedFields] = useState<ExtractedField[]>([]);
  const [valuesByColumn, setValuesByColumn] = useState<Record<string, string>>({});
  const [dataRoomUrl, setDataRoomUrl] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<EditingConfigField | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [gptProgressOpen, setGptProgressOpen] = useState(false);
  const [gptProgressSteps, setGptProgressSteps] = useState<GptProgressStep[]>(createInitialGptSteps);
  const [gptProgressError, setGptProgressError] = useState<string | null>(null);
  const [recommendedFiles, setRecommendedFiles] = useState<Record<string, RecommendedFileMeta>>({});
  const [recommendedFolderIds, setRecommendedFolderIds] = useState<Set<string>>(new Set());
  const [folderLoadSteps, setFolderLoadSteps] = useState<FolderLoadStep[]>(createInitialFolderLoadSteps);
  const [fieldEditErrors, setFieldEditErrors] = useState<Record<string, string>>({});

  const isBusy = phase === 'extracting' || phase === 'generating' || gptProgressOpen;
  const isAtFileSelectionLimit = selectedFileIds.size >= MAX_SELECTED_FILES;

  const loadFolderContents = useCallback(
    async (folderId: string, folderName: string) => {
      const url = savedBoxFolderUrl.trim();
      if (!url) return;

      setFolderContents((prev) => ({
        ...prev,
        [folderId]: {
          name: folderName,
          items: prev[folderId]?.items ?? [],
          loading: true,
          loaded: false,
          error: null,
        },
      }));

      try {
        const response = await fetch(`${API_BASE_URL}/data-tape/box-folder-contents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            boxFolderUrl: url,
            folderId,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.success !== true) {
          throw new Error(data.error || 'Failed to load folder contents.');
        }

        const folder = data.folder as { id: string; name: string };
        const items = (data.items as BoxFolderItem[]) || [];

        setFolderContents((prev) => ({
          ...prev,
          [folder.id]: {
            name: folder.name,
            items,
            loading: false,
            loaded: true,
            error: null,
          },
        }));

        return folder;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load folder contents.';
        setFolderContents((prev) => ({
          ...prev,
          [folderId]: {
            name: prev[folderId]?.name ?? 'Folder',
            items: prev[folderId]?.items ?? [],
            loading: false,
            loaded: false,
            error: message,
          },
        }));
        return null;
      }
    },
    [savedBoxFolderUrl]
  );

  const fetchFileRecommendations = useCallback(
    async (url: string) => {
      try {
        const response = await fetch(`${API_BASE_URL}/data-tape/box-recommend-files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ boxFolderUrl: url }),
        });
        const data = await response.json();
        if (!response.ok || data.success !== true) return;

        const recommendations = (data.recommendations as FileRecommendation[]) || [];
        if (recommendations.length === 0) return;

        const recMap: Record<string, RecommendedFileMeta> = {};
        const foldersInOrder: string[] = [];
        const seenFolders = new Set<string>();

        for (const rec of recommendations) {
          recMap[rec.fileId] = {
            relativePath: rec.relativePath,
            documentType: rec.documentType,
          };
          for (const folderId of rec.ancestorFolderIds) {
            if (!seenFolders.has(folderId)) {
              seenFolders.add(folderId);
              foldersInOrder.push(folderId);
            }
          }
        }

        for (const folderId of foldersInOrder) {
          await loadFolderContents(folderId, 'Folder');
        }

        setExpandedFolderIds((prev) => {
          const next = new Set(prev);
          for (const folderId of foldersInOrder) {
            next.add(folderId);
          }
          return next;
        });
        setRecommendedFiles(recMap);
        setRecommendedFolderIds(new Set(foldersInOrder));
      } catch {
        // No recommendations UI when the request fails.
      }
    },
    [loadFolderContents]
  );

  const handleLoadFolder = async () => {
    if (!soteriaProjectId.trim()) {
      setError('Please enter the Soteria Project Id.');
      return;
    }
    const url = boxFolderUrl.trim();
    if (!url) {
      setError('Please enter a Box folder URL.');
      return;
    }
    setSavedBoxFolderUrl(url);
    setSelectedFileIds(new Set());
    setSelectedFilesById({});
    setRecommendedFiles({});
    setRecommendedFolderIds(new Set());
    setRootFolder(null);
    setFolderContents({});
    setExpandedFolderIds(new Set());
    setExtractedFields([]);
    setValuesByColumn({});
    setDataRoomUrl(null);
    setPhase('idle');
    setError(null);
    setFolderLoadSteps(createInitialFolderLoadSteps());
    setIsLoadingRoot(true);

    try {
      setFolderLoadSteps((prev) => setFolderLoadStepActive(prev, 'loading-folder'));

      const response = await fetch(`${API_BASE_URL}/data-tape/box-folder-contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boxFolderUrl: url }),
      });
      const data = await response.json();
      if (!response.ok || data.success !== true) {
        throw new Error(data.error || 'Failed to load folder contents.');
      }

      const folder = data.folder as { id: string; name: string };
      const items = (data.items as BoxFolderItem[]) || [];

      setRootFolder(folder);
      setFolderContents({
        [folder.id]: {
          name: folder.name,
          items,
          loading: false,
          loaded: true,
          error: null,
        },
      });

      setFolderLoadSteps((prev) => setFolderLoadStepActive(prev, 'finding-documents'));
      await fetchFileRecommendations(url);
      setFolderLoadSteps((prev) => completeFolderLoadSteps(prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folder contents.');
      setRootFolder(null);
      setFolderContents({});
      setRecommendedFiles({});
      setRecommendedFolderIds(new Set());
    } finally {
      setIsLoadingRoot(false);
      setFolderLoadSteps(createInitialFolderLoadSteps());
    }
  };

  const toggleFolder = (folder: BoxFolderItem) => {
    if (isLoadingRoot) return;

    const isExpanded = expandedFolderIds.has(folder.id);

    if (isExpanded) {
      setExpandedFolderIds((prev) => {
        const next = new Set(prev);
        next.delete(folder.id);
        return next;
      });
      return;
    }

    setExpandedFolderIds((prev) => new Set(prev).add(folder.id));

    const existing = folderContents[folder.id];
    if (!existing?.loaded && !existing?.loading) {
      void loadFolderContents(folder.id, folder.name);
    }
  };

  const toggleFileSelection = (file: BoxFolderItem) => {
    if (!isSupportedDataroomFileName(file.name)) {
      setError(unsupportedDataroomFileMessage(file.name));
      return;
    }

    if (selectedFileIds.has(file.id)) {
      setError((prev) => (prev === FILE_SELECTION_LIMIT_MESSAGE ? null : prev));
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
      setSelectedFilesById((prev) => {
        const next = { ...prev };
        delete next[file.id];
        return next;
      });
      return;
    }

    if (selectedFileIds.size >= MAX_SELECTED_FILES) {
      setError(FILE_SELECTION_LIMIT_MESSAGE);
      return;
    }

    setError(null);
    setSelectedFileIds((prev) => new Set(prev).add(file.id));
    setSelectedFilesById((prev) => ({
      ...prev,
      [file.id]: { id: file.id, name: file.name },
    }));
  };

  const handleCloseGptProgress = () => {
    setGptProgressOpen(false);
    setGptProgressError(null);
    setGptProgressSteps(createInitialGptSteps());
    setPhase('idle');
  };

  const handleGptExtract = async () => {
    if (!soteriaProjectId.trim()) {
      setError('Please enter the Soteria Project Id.');
      return;
    }
    if (selectedFileIds.size === 0) {
      setError('Please select at least one file.');
      return;
    }

    const selectedFiles = Object.values(selectedFilesById).filter((f) =>
      selectedFileIds.has(f.id)
    );
    const unsupportedFile = selectedFiles.find((file) => !isSupportedDataroomFileName(file.name));
    if (unsupportedFile) {
      setError(unsupportedDataroomFileMessage(unsupportedFile.name));
      return;
    }

    setPhase('extracting');
    setError(null);
    setGptProgressError(null);
    setGptProgressSteps(createInitialGptSteps());
    setGptProgressOpen(true);

    try {
      const response = await fetch(`${API_BASE_URL}/data-tape/gpt-extract-from-files/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          soteriaProjectId: soteriaProjectId.trim(),
          boxFolderUrl: savedBoxFolderUrl.trim(),
          selectedFiles,
        }),
      });

      const { fields, valuesByColumn: values } = await consumeGptExtractStream(response, (event) => {
        setGptProgressSteps((prev) => applyGptProgressEvent(prev, event));
      });

      setGptProgressSteps((prev) =>
        prev.map((step) => (step.status === 'done' ? step : { ...step, status: 'done' }))
      );

      const resolvedFields = resolveExtractedFieldsForReview(fields);
      const resolvedValues = buildValuesByColumnFromFields(resolvedFields);
      const dataRoomField = resolvedFields.find((f) => f.fieldName.trim() === 'Data Room Link');
      setDataRoomUrl(dataRoomField?.value ?? null);
      setExtractedFields(resolvedFields);
      setValuesByColumn({ ...values, ...resolvedValues });
      setFieldEditErrors(buildFieldEditErrors(resolvedFields));
      setGptProgressOpen(false);
      setPhase('review');
    } catch (err) {
      const message = formatGptExtractError(err);
      setGptProgressError(message);
      setGptProgressSteps((prev) => {
        const activeIdx = prev.findIndex((s) => s.status === 'active');
        return prev.map((step, index) =>
          index === activeIdx ? { ...step, status: 'error', detail: message } : step
        );
      });
      setPhase('idle');
    }
  };

  const startEditing = (field: EditingConfigField) => {
    if (isBusy || isLoadingRoot) return;
    setEditingField(field);
    setEditDraft(field === 'projectId' ? soteriaProjectId : savedBoxFolderUrl);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditDraft('');
  };

  const saveProjectIdEdit = () => {
    const trimmed = editDraft.trim();
    if (!trimmed) {
      setError('Please enter the Soteria Project Id.');
      return;
    }
    setSoteriaProjectId(trimmed);
    setEditingField(null);
    setEditDraft('');
    setError(null);
  };

  const saveFolderUrlEdit = async () => {
    const trimmed = editDraft.trim();
    if (!trimmed) {
      setError('Please enter a Box folder URL.');
      return;
    }
    setBoxFolderUrl(trimmed);
    setEditingField(null);
    setEditDraft('');
    if (trimmed !== savedBoxFolderUrl) {
      setSavedBoxFolderUrl(trimmed);
      setSelectedFileIds(new Set());
      setSelectedFilesById({});
      setRecommendedFiles({});
      setRecommendedFolderIds(new Set());
      setRootFolder(null);
      setFolderContents({});
      setExpandedFolderIds(new Set());
      setExtractedFields([]);
      setValuesByColumn({});
      setDataRoomUrl(null);
      setPhase('idle');
      setError(null);
      setFolderLoadSteps(createInitialFolderLoadSteps());
      setIsLoadingRoot(true);
      try {
        setFolderLoadSteps((prev) => setFolderLoadStepActive(prev, 'loading-folder'));

        const response = await fetch(`${API_BASE_URL}/data-tape/box-folder-contents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ boxFolderUrl: trimmed }),
        });
        const data = await response.json();
        if (!response.ok || data.success !== true) {
          throw new Error(data.error || 'Failed to load folder contents.');
        }
        const folder = data.folder as { id: string; name: string };
        const items = (data.items as BoxFolderItem[]) || [];
        setRootFolder(folder);
        setFolderContents({
          [folder.id]: { name: folder.name, items, loading: false, loaded: true, error: null },
        });

        setFolderLoadSteps((prev) => setFolderLoadStepActive(prev, 'finding-documents'));
        await fetchFileRecommendations(trimmed);
        setFolderLoadSteps((prev) => completeFolderLoadSteps(prev));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load folder contents.');
        setRootFolder(null);
        setFolderContents({});
        setRecommendedFiles({});
        setRecommendedFolderIds(new Set());
      } finally {
        setIsLoadingRoot(false);
        setFolderLoadSteps(createInitialFolderLoadSteps());
      }
    } else {
      setError(null);
    }
  };

  const handleCloseReview = () => {
    setPhase('idle');
    setExtractedFields([]);
    setValuesByColumn({});
    setFieldEditErrors({});
    setDataRoomUrl(null);
  };

  const handleReviewFieldChange = (columnIndex: number, rawValue: string) => {
    const trimmed = rawValue.trim();
    let nextFields = extractedFields.map((field) =>
      field.columnIndex === columnIndex
        ? {
            ...field,
            value: trimmed === '' ? null : rawValue,
            error: null,
          }
        : field
    );

    nextFields = resolveExtractedFieldsForReview(nextFields);
    const nextValues = buildValuesByColumnFromFields(nextFields);

    setExtractedFields(nextFields);
    setValuesByColumn(nextValues);
    setFieldEditErrors(buildFieldEditErrors(nextFields));

    const dataRoomField = nextFields.find((f) => f.fieldName.trim() === 'Data Room Link');
    setDataRoomUrl(dataRoomField?.value?.trim() || null);
  };

  const handleApproveAndDownload = async () => {
    setPhase('generating');
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/data-tape/generate-from-values`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          valuesByColumn,
          dataRoomUrl: dataRoomUrl ?? undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.success !== true || !data.downloadUrl) {
        throw new Error(data.error || 'Failed to generate Data Tape.');
      }

      const downloadLink = `${API_BASE_URL}${data.downloadUrl}`;
      const anchor = document.createElement('a');
      anchor.href = downloadLink;
      anchor.download = 'Soteria_Data_Tape.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      setPhase('idle');
      setExtractedFields([]);
      setValuesByColumn({});
      setDataRoomUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate Data Tape.');
      setPhase('review');
    }
  };

  const foundCount = extractedFields.filter(
    (f) => f.value !== null && f.value.trim() !== ''
  ).length;
  const hasFieldEditErrors = Object.keys(fieldEditErrors).length > 0;

  const gptProgressPercentValue = gptProgressPercent(gptProgressSteps);
  const folderLoadProgressPercentValue = folderLoadProgressPercent(folderLoadSteps);

  const hasBrowser = rootFolder !== null && !isLoadingRoot;
  const selectedFilesList = Object.values(selectedFilesById)
    .filter((f) => selectedFileIds.has(f.id))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const renderTreeItems = (folderId: string, depth: number): React.ReactNode => {
    const contents = folderContents[folderId];
    if (!contents) return null;

    if (contents.error) {
      return (
        <div
          className="dataroom-tree-status error"
          style={{ paddingLeft: `${0.85 + depth * (TREE_INDENT_PX / 16)}rem` }}
        >
          {contents.error}
        </div>
      );
    }

    if (contents.items.length === 0 && contents.loaded) {
      return (
        <div
          className="dataroom-tree-status"
          style={{ paddingLeft: `${0.85 + depth * (TREE_INDENT_PX / 16)}rem` }}
        >
          Empty folder
        </div>
      );
    }

    return contents.items.map((item) => {
      if (item.type === 'folder') {
        const isExpanded = expandedFolderIds.has(item.id);
        const childContents = folderContents[item.id];
        const isRecommendedFolder = recommendedFolderIds.has(item.id);

        return (
          <div key={item.id} className="dataroom-tree-branch">
            <div
              className={`dataroom-tree-row folder-row ${isRecommendedFolder ? 'recommended' : ''}`}
              style={{ paddingLeft: `${0.85 + depth * (TREE_INDENT_PX / 16)}rem` }}
            >
              <button
                type="button"
                className={`dataroom-tree-expand ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleFolder(item)}
                disabled={isBusy || isLoadingRoot}
                aria-label={isExpanded ? `Collapse ${item.name}` : `Expand ${item.name}`}
                aria-expanded={isExpanded}
              >
                ›
              </button>
              <span className="dataroom-tree-icon">
                <FolderIcon />
              </span>
              <button
                type="button"
                className={`dataroom-tree-name folder-name-button ${
                  isRecommendedFolder ? 'recommended' : ''
                }`}
                onClick={() => toggleFolder(item)}
                disabled={isBusy || isLoadingRoot}
              >
                {item.name}
              </button>
            </div>
            {isExpanded && (
              <div className="dataroom-tree-children">
                {childContents?.loading ? (
                  <div
                    className="dataroom-tree-status"
                    style={{ paddingLeft: `${0.85 + (depth + 1) * (TREE_INDENT_PX / 16)}rem` }}
                  >
                    Loading…
                  </div>
                ) : (
                  renderTreeItems(item.id, depth + 1)
                )}
              </div>
            )}
          </div>
        );
      }

      const recommendation = recommendedFiles[item.id];
      const isRecommended = recommendation !== undefined;
      const showPath = isRecommended && recommendation.relativePath.includes('/');
      const displayName = showPath ? recommendation.relativePath : item.name;
      const isSelected = selectedFileIds.has(item.id);
      const isFileCheckboxDisabled =
        isBusy ||
        isLoadingRoot ||
        (isAtFileSelectionLimit && !isSelected);

      return (
        <label
          key={item.id}
          className={`dataroom-tree-row file-row ${isRecommended ? 'recommended' : ''} ${
            isFileCheckboxDisabled && !isSelected ? 'file-row-disabled' : ''
          }`}
          style={{ paddingLeft: `${0.85 + depth * (TREE_INDENT_PX / 16)}rem` }}
          title={isRecommended ? `${recommendation.documentType}: ${recommendation.relativePath}` : item.name}
        >
          <span className="dataroom-tree-expand-spacer" aria-hidden="true" />
          <input
            type="checkbox"
            className="dataroom-tree-checkbox"
            checked={isSelected}
            onChange={() => toggleFileSelection(item)}
            disabled={isFileCheckboxDisabled}
          />
          <span className="dataroom-tree-icon file-icon">
            <FileIcon />
          </span>
          <span className={`dataroom-tree-name ${isRecommended ? 'recommended' : ''}`}>
            {displayName}
          </span>
        </label>
      );
    });
  };

  return (
    <div className="BoxDataroomGenerator">
      <header className="BoxDataroomGenerator-header">
        <Link to="/" className="back-link">
          <img
            src="https://app.soteriamarket.com/banner3.jpeg"
            alt="Soteria Logo"
            className="logo"
          />
        </Link>
        <h1>Generate Datatape From Box Data Room (Single Project)</h1>
      </header>

      <main className="BoxDataroomGenerator-main">
        <div className="dataroom-panel">
          <h2>Box Data Room</h2>

          {!hasBrowser ? (
            <>
              <p className="dataroom-panel-intro">
                Provide the Soteria Project Id and the link to the relevant Box folder containing
                your loan documents.
              </p>

              <label className="dataroom-field" htmlFor="soteria-project-id">
                <span className="dataroom-field-label">Soteria Project Id</span>
                <input
                  id="soteria-project-id"
                  type="text"
                  className="dataroom-url-input"
                  placeholder="Enter Soteria Project Id"
                  value={soteriaProjectId}
                  onChange={(e) => setSoteriaProjectId(e.target.value)}
                  disabled={isBusy}
                  required
                />
              </label>

              <label className="dataroom-field" htmlFor="box-folder-url">
                <span className="dataroom-field-label">Box folder URL</span>
                <div className="dataroom-url-row">
                  <input
                    id="box-folder-url"
                    type="text"
                    className="dataroom-url-input"
                    placeholder="https://app.box.com/folder/..."
                    value={boxFolderUrl}
                    onChange={(e) => setBoxFolderUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleLoadFolder();
                    }}
                    disabled={isLoadingRoot || isBusy}
                  />
                  <button
                    type="button"
                    className="dataroom-load-button"
                    onClick={() => void handleLoadFolder()}
                    disabled={
                      !soteriaProjectId.trim() ||
                      !boxFolderUrl.trim() ||
                      isLoadingRoot ||
                      isBusy
                    }
                  >
                    {isLoadingRoot ? 'Loading…' : 'Load folder'}
                  </button>
                </div>
              </label>
            </>
          ) : (
            <div className="dataroom-config-compact">
              <div className="dataroom-config-row">
                <span className="dataroom-config-label">Project Id</span>
                {editingField === 'projectId' ? (
                  <input
                    type="text"
                    className="dataroom-config-input"
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveProjectIdEdit();
                      if (e.key === 'Escape') cancelEditing();
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="dataroom-config-value" title={soteriaProjectId}>
                    {soteriaProjectId}
                  </span>
                )}
                {editingField === 'projectId' ? (
                  <button
                    type="button"
                    className="dataroom-config-save"
                    onClick={saveProjectIdEdit}
                  >
                    Save
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dataroom-config-edit"
                    onClick={() => startEditing('projectId')}
                    disabled={isBusy || isLoadingRoot}
                    aria-label="Edit Soteria Project Id"
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>
              <div className="dataroom-config-row">
                <span className="dataroom-config-label">Box folder URL</span>
                {editingField === 'folderUrl' ? (
                  <input
                    type="text"
                    className="dataroom-config-input"
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveFolderUrlEdit();
                      if (e.key === 'Escape') cancelEditing();
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="dataroom-config-value" title={savedBoxFolderUrl}>
                    {savedBoxFolderUrl}
                  </span>
                )}
                {editingField === 'folderUrl' ? (
                  <button
                    type="button"
                    className="dataroom-config-save"
                    onClick={() => void saveFolderUrlEdit()}
                    disabled={isLoadingRoot}
                  >
                    {isLoadingRoot ? '…' : 'Save'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dataroom-config-edit"
                    onClick={() => startEditing('folderUrl')}
                    disabled={isBusy || isLoadingRoot}
                    aria-label="Edit Box folder URL"
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>
            </div>
          )}

          {error && <div className="dataroom-error">{error}</div>}

          {hasBrowser && (
            <div className="dataroom-browser">
              <p className="dataroom-browser-instruction">
                Expand folders to browse subfolders. Select up to {MAX_SELECTED_FILES} files where
                you want to extract data.
              </p>

              <div className="dataroom-tree-root-label">
                <FolderIcon />
                <span>{rootFolder?.name}</span>
              </div>

              <div className="dataroom-tree">
                {rootFolder && renderTreeItems(rootFolder.id, 0)}
              </div>

              {isAtFileSelectionLimit && (
                <div className="dataroom-selection-limit-notice">{FILE_SELECTION_LIMIT_MESSAGE}</div>
              )}

              {selectedFileIds.size > 0 && (
                <div className="dataroom-selection-summary">
                  <div className="dataroom-selection-count">
                    {selectedFileIds.size} of {MAX_SELECTED_FILES} file
                    {selectedFileIds.size === 1 ? '' : 's'} selected
                  </div>
                  <ul className="dataroom-selection-files">
                    {selectedFilesList.map((file) => (
                      <li key={file.id} className="dataroom-selection-file" title={file.name}>
                        {file.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="dataroom-generate-area">
                <button
                  type="button"
                  className="dataroom-generate-button gpt"
                  onClick={() => void handleGptExtract()}
                  disabled={
                    isBusy || !soteriaProjectId.trim() || selectedFileIds.size === 0
                  }
                >
                  Generate Soteria Data Tape
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {isLoadingRoot && (
        <div
          className="gpt-progress-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-load-progress-title"
        >
          <div className="gpt-progress-dialog">
            <h3 id="folder-load-progress-title">Loading data room</h3>
            <p className="gpt-progress-subtitle">
              Searching for BPO/Appraisal, Note, Loan Agreement, and Guarantee Agreement…
            </p>

            <CircularProgress percent={folderLoadProgressPercentValue} />

            <ol className="gpt-progress-steps">
              {folderLoadSteps.map((step) => (
                <li key={step.id} className={`gpt-progress-step status-${step.status}`}>
                  <span className="gpt-progress-step-icon" aria-hidden="true">
                    {step.status === 'done' ? (
                      '✓'
                    ) : step.status === 'active' ? (
                      <span className="gpt-progress-step-spinner" />
                    ) : (
                      '○'
                    )}
                  </span>
                  <span className="gpt-progress-step-text">
                    <span className="gpt-progress-step-label">{step.label}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {gptProgressOpen && (
        <div className="gpt-progress-overlay" role="dialog" aria-modal="true" aria-labelledby="gpt-progress-title">
          <div className="gpt-progress-dialog">
            <h3 id="gpt-progress-title">Generating with GPT</h3>
            <p className="gpt-progress-subtitle">
              Please wait while your selected files are processed.
            </p>

            <CircularProgress percent={gptProgressPercentValue} />

            <ol className="gpt-progress-steps">
              {gptProgressSteps.map((step) => (
                <li key={step.id} className={`gpt-progress-step status-${step.status}`}>
                  <span className="gpt-progress-step-icon" aria-hidden="true">
                    {step.status === 'done' ? (
                      '✓'
                    ) : step.status === 'error' ? (
                      '!'
                    ) : step.status === 'active' ? (
                      <span className="gpt-progress-step-spinner" />
                    ) : (
                      '○'
                    )}
                  </span>
                  <span className="gpt-progress-step-text">
                    <span className="gpt-progress-step-label">{step.label}</span>
                    {step.detail && (
                      <span className="gpt-progress-step-detail">{step.detail}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>

            {gptProgressError && (
              <div className="gpt-progress-error">{gptProgressError}</div>
            )}

            {gptProgressError && (
              <div className="gpt-progress-footer">
                <button
                  type="button"
                  className="gpt-progress-dismiss"
                  onClick={handleCloseGptProgress}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(phase === 'review' || phase === 'generating') && extractedFields.length > 0 && (
        <div className="dataroom-review-overlay" onClick={handleCloseReview}>
          <div className="dataroom-review-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dataroom-review-header">
              <div>
                <h3>Review extracted data</h3>
                <p className="dataroom-review-subtitle">
                  GPT found values for {foundCount} of {extractedFields.length} fields.
                  Edit any value before approving. Pick-list fields use a dropdown of allowed values.
                </p>
              </div>
              <button
                type="button"
                className="dataroom-review-close"
                onClick={handleCloseReview}
                aria-label="Close dialog"
                disabled={phase === 'generating'}
              >
                ×
              </button>
            </div>

            <div className="dataroom-review-content">
              <table className="dataroom-review-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedFields.map((field) => {
                    const hasValue = field.value !== null && field.value.trim() !== '';
                    const allowedValues = getEffectiveAllowedValues(field, extractedFields);
                    const inputValue = field.value ?? '';
                    const validationError = fieldEditErrors[String(field.columnIndex)];
                    const showNotFoundNote = !hasValue && !validationError;
                    const isDateField = field.validationRule?.kind === 'DATE';

                    return (
                      <tr
                        key={field.columnIndex}
                        className={hasValue && !validationError ? '' : 'dataroom-review-row-missing'}
                      >
                        <td className="dataroom-review-field">{field.fieldName}</td>
                        <td>
                          <div className="dataroom-review-edit-wrap">
                            <div className="dataroom-review-edit-row">
                              {allowedValues.length > 0 ? (
                                <select
                                  className={`dataroom-review-edit-input ${
                                    validationError ? 'has-error' : ''
                                  }`}
                                  value={inputValue}
                                  onChange={(e) =>
                                    handleReviewFieldChange(field.columnIndex, e.target.value)
                                  }
                                  disabled={phase === 'generating'}
                                >
                                  <option value="">— Select —</option>
                                  {inputValue && !allowedValues.includes(inputValue) && (
                                    <option value={inputValue}>{inputValue}</option>
                                  )}
                                  {allowedValues.map((allowedValue) => (
                                    <option key={allowedValue} value={allowedValue}>
                                      {allowedValue}
                                    </option>
                                  ))}
                                </select>
                              ) : isDateField ? (
                                <input
                                  className={`dataroom-review-edit-input dataroom-review-date-input ${
                                    validationError ? 'has-error' : ''
                                  }`}
                                  type="date"
                                  value={inputValue}
                                  onChange={(e) =>
                                    handleReviewFieldChange(field.columnIndex, e.target.value)
                                  }
                                  disabled={phase === 'generating'}
                                />
                              ) : (
                                <input
                                  className={`dataroom-review-edit-input ${
                                    validationError ? 'has-error' : ''
                                  }`}
                                  type="text"
                                  value={inputValue}
                                  placeholder="Enter value"
                                  onChange={(e) =>
                                    handleReviewFieldChange(field.columnIndex, e.target.value)
                                  }
                                  disabled={phase === 'generating'}
                                />
                              )}
                              {showNotFoundNote && (
                                <span className="dataroom-review-extraction-note">
                                  Could not find a value
                                </span>
                              )}
                            </div>
                            {validationError && (
                              <div className="dataroom-review-edit-error">{validationError}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="dataroom-review-footer">
              <button
                type="button"
                className="dataroom-review-approve"
                onClick={() => void handleApproveAndDownload()}
                disabled={
                  phase === 'generating' || foundCount === 0 || hasFieldEditErrors
                }
              >
                {phase === 'generating' ? 'Generating…' : 'Approve & Download Data Tape'}
              </button>
              <button
                type="button"
                className="dataroom-review-cancel"
                onClick={handleCloseReview}
                disabled={phase === 'generating'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GenerateDatatapeFromBoxDataroom;
